import React, { memo, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  ChartWrapper,
  CloseBtn,
  SChooseVariant,
  SCnt,
  Sdiv,
  SExpandBtn,
  ShrinkBtn,
  SPeriod,
  SToggleTooltipBtn,
} from './styles';

import { selectOptions } from './consts';
import { Tooltip as IconTooltip } from '../../../components/Tooltip/Tooltip';
import { Legend as LegendCnt, LegendOption } from '../../../components/Legend/Legend';

import { useDataset } from './hooks/useDataset/useDataset';
import { useLineOptions } from './hooks/useLineOptions/useLineOptions';
import { useTranslation } from 'react-i18next';
import keys from 'i18n/keys';
import { ReactComponent as ExpandIcon } from 'assets/Expand_icon.svg';
import { ReactComponent as ShrinkIcon } from 'assets/Shrink_icon.svg';
import { ReactComponent as CloseIcon } from 'assets/close_icon.svg';
import { ReactComponent as CursorIcon } from 'assets/Cursor.svg';
import moment from 'moment';
import { SIMULATOR_INTRO_STEPS } from 'pages/Simulator/TourSteps/consts';
import { PeriodType } from 'features/Period/types';
import { PeriodEnum } from 'services/types/PeriodEnum';
import { getObliczenia } from 'services/slices/obliczeniaSlice/selectors';
import { useAppDispatch, useAppSelector } from 'services/configuration/store/hooks';
import { getYearHours } from 'services/consts/months'; // Keep getYearHours from months
import { Select } from 'components/Select/Select';
import { Box, Card, Spacer } from 'components/common';
import { Button } from 'components/Button/Button';
import { LegendButton } from 'components/Legend/LegendButton';
import { setChartType, setViewMode } from 'services/slices/uiSlice';
import { PERIOD_OPTIONS } from 'services/consts/periodOptions'; // Import PERIOD_OPTIONS
import { getMonths } from 'services/consts/months'; // Import getMonths
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);
type LineEnergyProductionProps = {
  index?: number;
  isInModalView?: boolean;
  onOpenInModalMode?: () => void;
  onCloseModal?: (arg: any) => void;
  isPdfView?: boolean;
  id?: string;
  period?: PeriodType;
  legendExpanded?: boolean;
  currNumb?: number;
};

export const LineEnergyProduction = memo(
  ({
    index = 0,
    isInModalView = false,
    onOpenInModalMode,
    onCloseModal,
    id,
    period,
    currNumb,
    isPdfView,
  }: LineEnergyProductionProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { chartType, viewMode, periodIndex } = useAppSelector(state => state.ui);
    const selectedOption = useMemo(
      () => selectOptions.find(opt => opt.value === chartType) || selectOptions[index],
      [chartType, index],
    );

    const { selectedYear } = useAppSelector(getObliczenia);

    const labels = useMemo(
      () =>
        Array.from({ length: getYearHours(selectedYear) }, (_, i) =>
          moment.utc(`01-01-${selectedYear}`, 'DD-MM-YYYY').add(i, 'hour').format('DD-MM HH:mm'),
        ),
      [selectedYear],
    );

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isLegendExpanded, setIsLegendExpanded] = useState(true);

    const currentPeriodFromRedux: PeriodType = useMemo(() => {
      const periodOption = PERIOD_OPTIONS.find(el => el.value === viewMode) || PERIOD_OPTIONS[0];
      const targetValue = periodOption.values[periodIndex];

      let start = 0;
      let end = getYearHours(selectedYear) - 1; // Default for YEAR
      const currentTargetValue: string | number = targetValue; // Use targetValue directly

      switch (viewMode) {
        case PeriodEnum.QUATER: {
          const monthInQuater = getMonths(selectedYear).filter(el => el.quater === targetValue);
          start = monthInQuater[0].duration.start;
          end = monthInQuater[2].duration.end;
          break;
        }
        case PeriodEnum.MONTTH: {
          const month = getMonths(selectedYear)[periodIndex];
          start = month.duration.start;
          end = month.duration.end;
          // currentTargetValue remains targetValue, which is from periodOption.values[periodIndex]
          break;
        }
        case PeriodEnum.YEAR: {
          // Already defaulted
          break;
        }
      }
      return { start, end, target: viewMode, targetValue: currentTargetValue };
    }, [viewMode, periodIndex, selectedYear]);

    const filterByPeriod = (_, i) => {
      if (period) {
        return i >= period.start && i <= period.end;
      }
      return i >= currentPeriodFromRedux.start && i <= currentPeriodFromRedux.end;
    };

    const { dataset, legendOptions, optionsWithLabel, maxNumb } = useDataset({ selectedOption, filterByPeriod });

    const data: ChartData<'line', number[], string> = {
      labels: labels.filter(filterByPeriod),
      datasets: dataset,
    };

    const options = useLineOptions({
      period: period || currentPeriodFromRedux,
      selectedOption,
      isTooltipVisible,
      maxNumb,
    });

    const handleSelectChange = (val: typeof selectOptions[0]) => {
      dispatch(setChartType(val.value));
    };
    const handleOpenModalMode = () => {
      onOpenInModalMode?.();
    };
    const handleCloseModal = () => {
      onCloseModal?.(currNumb);
    };

    const toggleTooltipVisibility = () => {
      setIsTooltipVisible(!isTooltipVisible);
    };

    return (
      <div>
        <SCnt id={SIMULATOR_INTRO_STEPS.LINE_CHART} className="line-section">
          <SChooseVariant>
            <Box d="flex" gap="12px">
              <Select
                variant="outlined"
                options={selectOptions}
                value={selectedOption}
                onChange={handleSelectChange}
                startIcon={<IconTooltip id="line-energy-production" content={t(keys.LINE_CHART_SECTION_TOOLTIP)} />}
              />
            </Box>
            <SPeriod />
            {currNumb === 1 && (
              <ShrinkBtn variant="text" onClick={handleCloseModal}>
                <span>{t(keys.SHOW_SMALLER)}</span> <ShrinkIcon />
              </ShrinkBtn>
            )}
            {currNumb && currNumb > 1 && (
              <CloseBtn variant="outlined" onClick={handleCloseModal}>
                <span>{t(keys.REMOVE_FROM_VIEW)}</span>
                <CloseIcon />
              </CloseBtn>
            )}
          </SChooseVariant>
          <Sdiv id={id} className="line-chart-wrapper">
            <ChartWrapper className="chart-cnt">
              {!isPdfView && (
                <SToggleTooltipBtn tooltipVisible={isTooltipVisible} onClick={toggleTooltipVisibility}>
                  <CursorIcon />
                </SToggleTooltipBtn>
              )}
              <Line redraw={isInModalView} aria-label={t(selectedOption.label)} options={options} data={data} />
            </ChartWrapper>
            {isLegendExpanded && (
              <Box mt="12px">
                <LegendCnt options={legendOptions} optionsWithLabel={optionsWithLabel} />
              </Box>
            )}
          </Sdiv>
        </SCnt>
      </div>
    );
  },
);
